import React, { useState } from "react";
import { Sparkles, RefreshCw, ChevronLeft, Folder } from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  AppButton, AppModal, AiModelPicker, Callout, Checkbox, FormField, HelpText, Input, Textarea, cn,
} from "../ui";
import useAiModels from "../../hooks/useAiModels";
import AiDialogSteps from "./AiDialogSteps";

const MAX_SUBTOPICS = 20;

// The submit button sits in the modal's footer, outside the form element.
const FORM_ID = "quiz-ai-subtopics-form";

/**
 * GenerateAiSubtopicsModal — AI-suggested subtopic folders for a category.
 * The category comes preselected; the admin describes what the subtopics
 * should cover (or leaves it blank and the AI derives them from the category)
 * and picks how many (Describe), then reviews the suggestions and saves the
 * keepers (Review). Existing subtopics are never repeated.
 *
 * BOTH roles pick a model from the shared catalogue (useAiModels →
 * /quiz-questions/ai-models); institute generations draw from the institute AI
 * token pool.
 */
const GenerateAiSubtopicsModal = ({ category, onClose, onSaved }) => {
  const ai = useAiModels("/quiz-questions/ai-models");
  const { models, quota, enabled: aiEnabled } = ai;
  const [form, setForm] = useState({ count: 8, summary: "" });
  const [step, setStep] = useState("form"); // form | review
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState({ subtopics: [], model: "" });
  const [picked, setPicked] = useState(() => new Set());

  const quotaBlockedMsg =
    quota && quota.allowed === false
      ? quota.reason === "quota_exhausted"
        ? "Your institute has used all its AI tokens for this month."
        : "AI generation is not available on your current plan."
      : "";

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    setError("");
    try {
      const { data } = await apiClient.post("/quiz-categories/generate-ai", {
        category: category._id,
        count: Math.min(Math.max(Number(form.count) || 8, 1), MAX_SUBTOPICS),
        summary: form.summary.trim(),
        ...ai.payload,
      });
      // `data.model` is what RAN — the server falls back when the account
      // cannot reach the picked model, and the admin should see which one wrote
      // these before deciding to keep them.
      setResult({ subtopics: data.subtopics || [], model: data.model || ai.model });
      setPicked(new Set((data.subtopics || []).map((_, i) => i)));
      setStep("review");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to generate subtopics.");
    }
    setGenerating(false);
  };

  const togglePick = (i) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const handleSave = async () => {
    const keep = result.subtopics.filter((_, i) => picked.has(i));
    if (!keep.length) return;
    setSaving(true);
    setError("");
    try {
      const { data } = await apiClient.post("/quiz-categories/bulk-subcategories", {
        category: category._id,
        subtopics: keep,
      });
      onSaved(data.message || `${keep.length} subtopic(s) created.`);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create subtopics.");
      setSaving(false);
    }
  };

  const modelLabel = models.find((m) => m.id === result.model)?.label || result.model;

  const footer = step === "form" ? (
    <>
      <AppButton variant="outline" type="button" onClick={onClose} disabled={generating}>
        Cancel
      </AppButton>
      <AppButton
        variant="default"
        type="submit"
        form={FORM_ID}
        disabled={generating || !aiEnabled || !models.length || !!quotaBlockedMsg}
      >
        {generating
          ? <><RefreshCw size={14} className="tw-animate-spin" aria-hidden="true" /> Generating…</>
          : <><Sparkles size={14} aria-hidden="true" /> Generate</>}
      </AppButton>
    </>
  ) : (
    <>
      <AppButton variant="outline" type="button" onClick={onClose} disabled={saving}>
        Discard
      </AppButton>
      <AppButton variant="default" type="button" onClick={handleSave} disabled={saving || picked.size === 0}>
        {saving ? "Creating…" : `Create ${picked.size} subtopic${picked.size === 1 ? "" : "s"}`}
      </AppButton>
    </>
  );

  return (
    <AppModal
      show
      onHide={() => !generating && !saving && onClose()}
      size="lg"
      icon={<Sparkles size={18} className="tw-align-[-3px] tw-text-chart-1" aria-hidden="true" />}
      title="Generate subtopics with AI"
      footer={footer}
    >
      <AiDialogSteps step={step} />

      {step === "form" ? (
        <form id={FORM_ID} onSubmit={handleGenerate} className="tw-flex tw-flex-col tw-gap-4">
          <p className="tw-m-0 tw-text-sm tw-text-content">
            New subtopics go into <strong>{category?.name}</strong>.
          </p>

          <FormField label="Number of subtopics" hint={`Up to ${MAX_SUBTOPICS} at a time.`}>
            <Input
              type="number"
              min="1"
              max={MAX_SUBTOPICS}
              value={form.count}
              onChange={(e) => setForm((f) => ({ ...f, count: e.target.value }))}
              required
              className="tw-max-w-[10rem]"
            />
          </FormField>

          <FormField
            label="Desired subtopics or a summary"
            hint="Optional — leave blank and the AI derives the key subtopics of this category."
          >
            <Textarea
              rows={3}
              placeholder="e.g. focus on SQL basics, joins and query tuning"
              value={form.summary}
              onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
              maxLength={1000}
            />
          </FormField>

          <AiModelPicker {...ai} />

          {quota && quota.allowed !== false && quota.limit > 0 && (
            <HelpText>
              Institute AI tokens: {quota.used.toLocaleString()} / {quota.limit.toLocaleString()} used
              {" · "}{quota.remaining.toLocaleString()} left this month
            </HelpText>
          )}

          <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-muted">
            Existing subtopics of this category are never repeated. You review the suggestions before
            anything is created.
          </p>

          {quotaBlockedMsg && <Callout tone="danger" role="alert">{quotaBlockedMsg}</Callout>}
          {error && <Callout tone="danger" role="alert">{error}</Callout>}
        </form>
      ) : (
        <div className="tw-flex tw-flex-col tw-gap-3">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-text-[0.8125rem] tw-text-content-muted">
            <span>
              {result.subtopics.length} suggested by <strong className="tw-text-content">{modelLabel}</strong>
              {" · "}{picked.size} selected — untick any you do not want.
            </span>
            <AppButton variant="ghost" size="sm" type="button" onClick={() => setStep("form")}>
              <ChevronLeft size={14} aria-hidden="true" /> Change settings
            </AppButton>
          </div>

          <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
            {result.subtopics.map((s, i) => (
              <li
                key={i}
                className={cn(
                  "tw-flex tw-items-start tw-gap-3 tw-rounded-md tw-border tw-border-solid tw-border-line tw-p-3 tw-transition-opacity",
                  !picked.has(i) && "tw-opacity-55",
                )}
              >
                <Checkbox
                  checked={picked.has(i)}
                  onChange={() => togglePick(i)}
                  aria-label={`Keep ${s.name}`}
                  className="tw-mt-0.5"
                />
                <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1">
                  <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-medium tw-text-content">
                    <Folder size={14} aria-hidden="true" className="tw-text-content-muted" /> {s.name}
                  </span>
                  {s.description && <p className="tw-m-0 tw-text-xs tw-text-content-subtle">{s.description}</p>}
                </div>
              </li>
            ))}
          </ul>

          {error && <Callout tone="danger" role="alert">{error}</Callout>}
        </div>
      )}
    </AppModal>
  );
};

export default GenerateAiSubtopicsModal;
